/**
 * The look of an AG Grid embedded inside a drilldown card (Liquidity Calc,
 * DSCR Calc, …): the same table system the Admin → DSCR Pricing screen uses —
 * a bordered rounded block, small-caps faint headers, hairline row rules, no
 * column separators, monospaced tabular figures in any `cellClass: "num"`
 * column — so a table reads the same whether it sits on an admin page or in a
 * loan tab. There is one definition of that look (`admin/shared/adminGrid`);
 * this module only adapts it to the card context.
 *
 *   - `CARD_TABLE_BOX_SX`  — the bordered block a table sits in.
 *   - `CARD_GRID_SX`       — for a grid the tab owns (pass to AgGridTableWrapper).
 *   - `CARD_FILES_GRID_SX` — for an embedded `LoanDocumentManagerTable` (pass
 *                            as `gridSx`); a subset, because that table carries
 *                            its own row heights, cell padding and sticky header.
 */
import {
  ADMIN_GRID_SX,
  ADMIN_GRID_COL_DEF,
  GRID_HEADER_H,
  GRID_ROW_H,
} from "../../admin/shared/adminGrid";
import { level1 } from "../../../theme/colors";

export { GRID_HEADER_H, GRID_ROW_H };

// A source row on the calc tabs carries a label AND a sub-line (notes, the
// component type, the unit) — the admin 36px row clips the second line.
export const CARD_GRID_TWO_LINE_ROW_H = 44;

// The admin grid's two line weights: the block edge, and the softer rule
// between rows (kept in step with `adminGrid.js`).
const LINE = "#e5e7eb";
const LINE_SOFT = "#eef2f6";

export const CARD_TABLE_BOX_SX = {
  border: `1px solid ${LINE}`,
  borderRadius: "8px",
  // `clip`, NOT `hidden`: both clip to the radius, but `hidden` establishes a
  // scroll container, which traps LoanDocumentManagerTable's sticky toolbar
  // inside this box instead of letting it stick to the page (its own comment
  // spells this out).
  overflow: "clip",
};

export const CARD_GRID_SX = ADMIN_GRID_SX;

export const CARD_GRID_COL_DEF = ADMIN_GRID_COL_DEF;

// Only the header + row-rule slice of the look. `&&` doubles the specificity so
// these out-rank the table's own `& .ag-header` / `& .ag-header-cell` rules
// without replacing those keys (a same-named key in a merged sx would drop the
// sticky positioning that lives alongside the header background).
const HEADER_TEXT = ADMIN_GRID_SX[".ag-header-cell-text"];
export const CARD_FILES_GRID_SX = {
  "--ag-header-column-separator-display": "none",
  "--ag-row-border-color": LINE_SOFT,
  "&& .ag-header": {
    // White, not transparent: the header is sticky in autoHeight mode and the
    // rows scroll under it.
    background: "#fff",
    minHeight: `${GRID_HEADER_H}px`,
  },
  "&& .ag-header-row": { minHeight: `${GRID_HEADER_H}px` },
  "&& .ag-header-cell": { ...HEADER_TEXT },
  "&& .ag-header-cell .ag-header-cell-text": { ...HEADER_TEXT },
  "&& .ag-header-cell:hover, && .ag-header-cell.ag-header-cell-sorted-asc, && .ag-header-cell.ag-header-cell-sorted-desc":
    { color: ADMIN_GRID_SX[".ag-cell"].color },
  "&& .ag-row": { borderBottomColor: LINE_SOFT },
  "&& .ag-row-last": { borderBottom: "none" },
};

// The row whose edit sidebar is open: the same tint + rail the admin grid gives a
// selected row (level-1 tokens), so "which row am I editing" reads the same on
// every card table. Return it from `getRowStyle` for the active row.
export const CARD_GRID_ACTIVE_ROW_STYLE = {
  background: level1.tint,
  boxShadow: `inset 3px 0 0 ${level1.indicator}`,
};
